import { type ParseResult } from 'papaparse'
import { isValidEmail } from '../../../common/util'
import { type RoleType } from '../../../models/Role'
import { getInviteRoleGroups } from '../InviteRoleGroups'

export const BULK_INVITE_ROW_LIMIT = 50

const _REQUIRED_COLUMNS = ['email', 'firstName', 'lastName', 'role'] as const

export interface RawBulkCsvRow {
  email?: string
  firstName?: string
  lastName?: string
  role?: string
}

export interface BulkCsvRow {
  index: number
  email: string
  firstName: string
  lastName: string
  role: string
  roleIds: string[]
  seatType: RoleType | null
  errors: string[]
}

export type BulkCsvRows = { kind: 'fileError'; message: string } | { kind: 'rows'; rows: BulkCsvRow[] }

const isEmptyRow = (row: RawBulkCsvRow) => _REQUIRED_COLUMNS.every((column) => !row[column]?.trim())

export const getBulkCsvRows = (result: ParseResult<RawBulkCsvRow>): BulkCsvRows => {
  if (result.errors.length > 0) {
    return { kind: 'fileError', message: `Invalid CSV: ${result.errors.map((error) => error.message).join(', ')}` }
  }

  const missingColumns = _REQUIRED_COLUMNS.filter((column) => !result.meta.fields?.includes(column))
  if (missingColumns.length > 0) {
    return { kind: 'fileError', message: `Missing columns: ${missingColumns.join(', ')}` }
  }

  const rawRows = result.data.filter((row) => !isEmptyRow(row))
  if (rawRows.length === 0) {
    return { kind: 'fileError', message: 'The CSV file has no rows' }
  }
  if (rawRows.length > BULK_INVITE_ROW_LIMIT) {
    return { kind: 'fileError', message: `Too many rows: the limit is ${BULK_INVITE_ROW_LIMIT}, the file has ${rawRows.length}` }
  }

  const inviteRoleGroups = getInviteRoleGroups({ onlyInvitable: true })
  const seenEmails = new Set<string>()

  const rows = rawRows.map((rawRow, index) => {
    const email = rawRow.email?.trim() ?? ''
    const firstName = rawRow.firstName?.trim() ?? ''
    const lastName = rawRow.lastName?.trim() ?? ''
    const role = rawRow.role?.trim() ?? ''
    const roleGroup = inviteRoleGroups.find((group) => group.name === role) ?? null

    const errors: string[] = []
    if (!email) {
      errors.push('Email is missing')
    } else if (!isValidEmail(email)) {
      errors.push('Invalid email address')
    } else if (seenEmails.has(email.toLowerCase())) {
      errors.push('Duplicate email in the file')
    } else {
      seenEmails.add(email.toLowerCase())
    }
    if (!firstName) {
      errors.push('First name is missing')
    }
    if (!lastName) {
      errors.push('Last name is missing')
    }
    if (!role) {
      errors.push('Role is missing')
    } else if (!roleGroup) {
      errors.push(`Unknown role: ${role}`)
    }

    return {
      index,
      email,
      firstName,
      lastName,
      role,
      roleIds: roleGroup?.roleIds ?? [],
      seatType: roleGroup?.type ?? null,
      errors,
    }
  })

  return { kind: 'rows', rows }
}
